import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from "react-router-dom";
import { Link } from 'react-router-dom';
import {localizeKey, localizeArt} from './../localize.js';
import axiosInstance from './../axiosInstance.js';
import MyUserContext from './../myUserContext.js';
import TagBadge, { tagBadgeColor, tagName } from "./TagBadge";

const Notifications = ( {unreadCountCallback} ) => {
  const myUser = useContext(MyUserContext);

  const [notifications, setNotifications] = useState(null);
  const [unreadCount, setUnreadCount] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const fetchNotifications = async () => {
    try {
      const firstParams = new URLSearchParams();
      firstParams.set('count', '10');
      const firstResponse = await axiosInstance.get(`/notifications?${firstParams.toString()}`);
      setNotifications(firstResponse.data);

      const secondParams = new URLSearchParams();
      secondParams.set('count', '10000');
      secondParams.set('offset', '10');
      setIsLoadingMore(true);
      const secondResponse = await axiosInstance.get(`/notifications?${secondParams.toString()}`);
      setIsLoadingMore(false);
      if (Array.isArray(secondResponse.data) && secondResponse.data.length > 0) {
        setNotifications((prev) => (prev || []).concat(secondResponse.data));
      }
    } catch (error) {
      setIsLoadingMore(false);
      console.error(error);
    }
  };

  const markAsRead = (id) => {
    axiosInstance
    .post(`/notifications/${id}/markAsRead`)
    .then((response) => {
      const newUnreadCount = Math.max(0, unreadCount - 1);
      unreadCountCallback(newUnreadCount);
      setUnreadCount(newUnreadCount);
      fetchNotifications();
    })
    .catch((error) => {
      console.error('Failed to mark notification as read:', error);
    })
  };

  const fetchUnreadCount = () => {
    axiosInstance
    .get('/notifications/unreadCount')
    .then((response) => {
      setUnreadCount(response.data.unreadCount);
      unreadCountCallback(response.data.unreadCount);
    })
    .catch((error) => {
      console.error('Failed to fetch unread notification count:', error);
    })
  };

  const parseStringToBold = (text) => {
    const parts = text.split(/(\*[^*]+\*)/); // Split the string by *...* pattern

    return parts.map((part, index) => {
      if (part.startsWith('*') && part.endsWith('*')) {
        return <strong key={index}>{part.slice(1, -1)}</strong>;
      } else {
        return part;
      }
    });
  };

  const groupNotifications = (notifications) => {
    const GROUP_THRESHOLD = 3; // Threshold for ungrouping notifications

    const grouped = [];
    let currentGroup = [];

    notifications.forEach((notification, index) => {
      if (
        currentGroup.length > 0 &&
        currentGroup[0].initiatingUserId === notification.initiatingUserId &&
        currentGroup[0].kind === notification.kind
      ) {
        currentGroup.push(notification);
      } else {
        if (currentGroup.length > 0) {
          // Ungroup if there are GROUP_THRESHOLD or more notifications in the current group
          if (currentGroup.length < GROUP_THRESHOLD) {
            grouped.push(...currentGroup.map((item) => [item])); // Wrap each item in an array
          } else {
            grouped.push(currentGroup);
          }
        }
        currentGroup = [notification];
      }
    });

    if (currentGroup.length > 0) {
      // Ungroup if there are GROUP_THRESHOLD or more notifications in the final group
      if (currentGroup.length < GROUP_THRESHOLD) {
        grouped.push(...currentGroup.map((item) => [item])); // Wrap each item in an array
      } else {
        grouped.push(currentGroup);
      }
    }

    return grouped;
  };

  const notificationTitle = (notification) => {
    const baseTitle = notification.title;
    const isFinished = notification.notification && notification.notification.finished === true;
    if (!isFinished) {
      return baseTitle;
    }
    if (notification.kind === "user.watched.something") {
      return language === "ru" ? "посмотрел(а) фильм" : "finished the movie";
    }
    if (notification.kind === "user.read.something") {
      return language === "ru" ? "прочитал(а) книгу" : "finished the book";
    }
    if (notification.kind === "user.played.something") {
      return language === "ru" ? "прошел(а) игру" : "finished the game";
    }
    return baseTitle;
  };

  const NotificationCard = ({ notificationGroup }) => {
    const navigate = useNavigate();
    const [isExpanded, setIsExpanded] = useState(false);

    const firstNotification = notificationGroup[0];
    const ntfText = notificationTitle(firstNotification);
    const link = firstNotification.linkToEvent;

    const handleNotificationClick = (notification) => {
      markAsRead(notification.notification.id);
      navigate(notification.linkToEvent);
    };

    const localizedShowMore = (count) => {
      return language === "ru" ? `Показать еще ${count}` : `Show ${count} more`;
    };

    const localizedHide = () => {
      return language === "ru" ? "Скрыть" : "Hide";
    };

    const renderEventTags = (tags) => {
      if (!tags || tags.length === 0) {
        return null;
      }

      return (
        <div class="mt-1 flex flex-wrap gap-1">
          {tags.map((tag) => (
            <TagBadge
              key={tag.id}
              size="xs"
              radius="sm"
              variant={tag.type === 5 ? "light" : "filled"}
              color={tagBadgeColor(tag.name, tag.type)}
            >
              {tagName(tag.name, language)}
            </TagBadge>
          ))}
        </div>
      );
    };

    const renderEventMessages = (messages) => {
      if (!messages || messages.length === 0) {
        return null;
      }

      return (
        <div class="mt-2 pt-2 border-t border-black/10">
          <div class="text-[10px] uppercase tracking-wide opacity-70 mb-2">
            {language === "ru" ? "Сообщения из чата" : "Chat messages"}
          </div>
          <div class="flex flex-col gap-2">
          {[...messages].reverse().map((message, index) => (
            <div key={index} class="flex items-start gap-2 text-xs">
              <img class="w-6 h-6 rounded-full" src={message.userAvatar} />
              <div class="flex-1 leading-snug">
                <span class="font-semibold mr-1">{message.userName}</span>
                <span class="opacity-90">{message.text}</span>
              </div>
            </div>
          ))}
          </div>
        </div>
      );
    };

    const renderArtTitle = (notification) => {
      if (!notification.artTitle) {
        return null;
      }
      const yearPart = notification.artYear ? ` (${notification.artYear})` : "";
      return (
        <div class="text-sm font-semibold">
          {notification.artTitle}
          {yearPart}
        </div>
      );
    };

    const renderLastLogbookTime = (notification) => {
      if (!notification.lastLogbookTime || !notification.lastLogbookLabel) {
        return null;
      }
      const formatted = new Date(notification.lastLogbookTime).toLocaleDateString(language, {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      return (
        <div class="text-xs opacity-70 mt-1">
          {notification.lastLogbookLabel}: {formatted}
        </div>
      );
    };

    const renderDescriptionPreview = (description) => {
      if (!description) {
        return null;
      }
      const paragraphs = description.split(/\r?\n/);
      const firstParagraph = paragraphs.find((p) => p.trim() !== "");
      if (!firstParagraph) {
        return null;
      }
      const sentences = firstParagraph.match(/[^.!?]+[.!?]+/g) || [firstParagraph];
      const preview = sentences.join(" ").trim();
      const limit = 150;
      const trimmed = preview.length > limit ? preview.slice(0, limit).trim() + "..." : preview;

      return (
        <div class="text-xs opacity-80 mt-1">
          {trimmed}
        </div>
      );
    };

    return (
      <div class="rounded-xl overflow-hidden">
        <div
          class={`hover:cursor-pointer rounded-xl p-3 ${
            firstNotification.notification.read === false
              ? "bg-white shadow-sm hover:bg-gray-50"
              : "bg-white/60 hover:bg-white"
          }`}
          onClick={() => handleNotificationClick(firstNotification)}
        >
          <div class="flex gap-3 items-start">
            <img class="rounded-lg w-[72px] h-[96px] object-cover flex-shrink-0" src={firstNotification.artImageUrl} />
            <div class="flex-1 min-w-0 text-sm leading-relaxed">
              <div class="flex items-center gap-2 mb-1">
                {firstNotification.initiatingUserName ? (
                  <>
                    <img class="w-8 h-8 rounded-full flex-shrink-0" src={firstNotification.initiatingUserAvatarUrl} />
                    <div class="text-sm">
                      {parseStringToBold("*" + firstNotification.initiatingUserName + "* " + ntfText)}
                    </div>
                  </>
                ) : (
                  <div class="text-sm">{parseStringToBold("*" + ntfText + "*")}</div>
                )}
              </div>
              {renderArtTitle(firstNotification)}
              {renderDescriptionPreview(firstNotification.artDescription)}
              {renderLastLogbookTime(firstNotification)}
              {renderEventTags(firstNotification.eventTags)}
              {renderEventMessages(firstNotification.eventMessages)}
            </div>
          </div>
        </div>
        {notificationGroup.length > 1 && (
          <div class="ml-4 mt-1">
            <button onClick={() => setIsExpanded(!isExpanded)} class="text-xs py-1 text-blue-500">
              {isExpanded ? localizedHide() : localizedShowMore(notificationGroup.length - 1)}
            </button>
            {isExpanded && (
              <ul class="flex flex-col gap-2 mt-2">
                {notificationGroup.slice(1).map((notification) => (
                  <li
                    key={notification.notification.id}
                    class={`hover:cursor-pointer rounded-xl p-3 ${
                      notification.notification.read === false
                        ? "bg-white shadow-sm hover:bg-gray-50"
                        : "bg-white/60 hover:bg-white"
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div class="flex gap-3 items-start">
                      <img class="rounded-lg w-[72px] h-[96px] object-cover flex-shrink-0" src={notification.artImageUrl} />
                      <div class="flex-1 min-w-0 text-sm leading-relaxed">
                        <div class="flex items-center gap-2 mb-1">
                          {notification.initiatingUserName ? (
                            <>
                              <img class="w-8 h-8 rounded-full flex-shrink-0" src={notification.initiatingUserAvatarUrl} />
                              <div class="text-sm">
                                {parseStringToBold("*" + notification.initiatingUserName + "* " + notificationTitle(notification))}
                              </div>
                            </>
                          ) : (
                            <div class="text-sm">{parseStringToBold("*" + notificationTitle(notification) + "*")}</div>
                          )}
                        </div>
                        {renderArtTitle(notification)}
                        {renderDescriptionPreview(notification.artDescription)}
                        {renderLastLogbookTime(notification)}
                        {renderEventTags(notification.eventTags)}
                        {renderEventMessages(notification.eventMessages)}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    );
  };

  const language = myUser ? myUser.myUserSettings.lang : 'ru';

  if (!myUser) {
    return (
      <div class="flex mt-2 md:mt-4 justify-center items-center text-center">
        {
        (language === "ru") ?
        <>
          Здесь будут уведомления
        </>
        :
        <>
          Here will be notifications
        </>
        }
      </div>
    );
  }

  if (notifications === null && unreadCount === null) {
    fetchNotifications();
    fetchUnreadCount();
  }
  if (notifications === null || unreadCount === null) {
    return (
      <div></div>
    )
  }

  const groupedNotifications = notifications.map((notification) => [notification]);

  return (
    <div class="container mx-auto w-full lg:w-1/2">
      {
      (groupedNotifications && groupedNotifications.length == 0) &&
      <div class="flex mt-4 justify-center items-center text-center text-gray-500">
        {
        (language === "ru") ?
        <>
          У вас пока нет уведомлений.<br/>
          Подписывайтесь на других пользователей, чтобы получать их.
        </>
        :
        <>
          No notifications yet.<br/>
          Subscribe to other users to receive notifications.
        </>
        }
      </div>
      }

      {
      (groupedNotifications && groupedNotifications.length > 0) &&
      <ul className="flex flex-col gap-3 mt-3">
        {groupedNotifications.map((notificationGroup, index) => (
          <li key={index}>
              <NotificationCard notificationGroup={notificationGroup}/>
          </li>
        ))}
      </ul>
      }
      {isLoadingMore && (
        <div class="flex justify-center text-center">
          <div class="w-full max-w-md">
            <div class="h-2 w-full overflow-hidden rounded-full bg-gray-200">
              <div class="similars-progress-bar h-full w-1/3 rounded-full bg-gray-300"></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications;
